import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDesignStore } from '../store/designStore';
import { crossOriginForUrl } from '../lib/crossOriginImage';
import { FULL_COLOR, printColorOptionToString, parsePrintColorOptions, capitalizeLabel, type PrintColorOption, parsePrintAreaSizeOptions } from '@customedge/shared';
import { translatedMethodLabel, translatedPositionLabel, translatedColorOptionLabel } from '../lib/translatedLabels';
import { notifyParent } from '../hooks/useDesignerInit';

interface ProductOptionsModalProps {
  onClose: () => void;
}

// Customer-facing "Product options" popup — collects Product color (a vendor
// Product Color Variant), Print method, and Print colors in one place,
// triggered from the DesignerHeader's "ProductName | Color ▾" control.
// Structurally modeled on DesignerHeader.tsx's own showTemplateModal (fixed
// full-screen scrim + centered white card, scrim click / stopPropagation).
export function ProductOptionsModal({ onClose }: ProductOptionsModalProps) {
  const { t } = useTranslation();
  const {
    productConfig, colorVariants, activeColorVariantId, setActiveColorVariant,
    selectedPrintMethod, setSelectedPrintMethod,
    selectedPrintColorOption, setSelectedPrintColorOption,
    selectedPrintPositions, setSelectedPrintPositions,
    selectedPrintMethodByPosition, setSelectedPrintMethodByPosition,
    selectedPrintColorByPosition, setSelectedPrintColorByPosition,
    selectedPrintAreaSizeByPosition, setSelectedPrintAreaSizeByPosition,
  } = useDesignStore();

  // Local staged selections — only committed to the store on Apply, so
  // closing via the scrim/× without hitting Apply discards changes, same
  // convention as showTemplateModal's local `templateTitle` state.
  const [stagedColorId, setStagedColorId] = useState(activeColorVariantId);
  const [stagedMethod, setStagedMethod] = useState(selectedPrintMethod);
  const [stagedPrintColor, setStagedPrintColor] = useState<PrintColorOption | null>(selectedPrintColorOption);
  // Multiselect — toggled on/off per click, not radio-exclusive like
  // stagedMethod above. Each staged position gets its own independently
  // staged print method/color below (stagedMethodByPosition/stagedColorByPosition).
  const [stagedPositions, setStagedPositions] = useState<string[]>(selectedPrintPositions);
  const [stagedMethodByPosition, setStagedMethodByPosition] = useState<Record<string, string>>(selectedPrintMethodByPosition);
  const [stagedColorByPosition, setStagedColorByPosition] = useState<Record<string, PrintColorOption>>(selectedPrintColorByPosition);
  const [stagedAreaSizeByPosition, setStagedAreaSizeByPosition] = useState<Record<string, number>>(selectedPrintAreaSizeByPosition);

  // Which position's tab is active in the picker below — always one of
  // stagedPositions, each with its own independent method+color. There used
  // to be a "Voor alle"/"For all" tab that stamped one shared method+color
  // onto every staged position at once, but print method genuinely depends
  // on print position (not every method is valid at every position) and
  // print color depends on the chosen method — a single "apply to all"
  // action could silently stage an invalid method/color at a position that
  // doesn't support it (only caught after the fact by a correction effect).
  // Removed entirely so every position is always chosen on its own terms.
  // Not persisted in designStore — this is purely a UI display mode local to
  // this modal session, the actual data (stagedMethodByPosition/
  // stagedColorByPosition) is the only thing that matters once Apply commits.
  const [activePositionTab, setActivePositionTab] = useState<string>(stagedPositions[0] ?? '');

  const printMethods = productConfig?.printMethods ?? [];
  const printPositions = productConfig?.printPositions ?? [];

  // All sides across the base product and every color variant — same
  // collection allSidesForStagedMethod below builds, hoisted here so the
  // method-chip list can also be scoped by position (a product's print
  // method is a property of its PRINT AREA/side, e.g. PX118032's
  // "achterzijde" only ever offers "Full color doming" while "voorzijde"
  // offers 5 methods — see ImageSideConfig.printMethod/printPosition).
  const allSides = [
    ...(productConfig?.imageSides ?? []),
    ...colorVariants.flatMap(cv => cv.imageSides),
  ];

  // Print methods valid for a given PRINT POSITION — mirrors
  // sideAreaSizeOptionsOverride's reasoning below (a method valid for one
  // position is not necessarily valid for another, e.g. PX118032's back
  // only supports doming). A side with no printPosition is position-agnostic
  // (see useProductSetup.ts's byPositionParam) and its method is offered
  // under every position. Falls back to the flat product-wide list when no
  // side carries a printMethod/printPosition tag at all, so a product not
  // yet reloaded with per-side data behaves byte-identically to before this
  // fix.
  function methodsForPosition(position: string): string[] {
    const taggedMethods = new Set(
      allSides
        .filter(side => !side.printPosition || side.printPosition === position)
        .map(side => side.printMethod)
        .filter((m): m is string => Boolean(m))
    );
    return taggedMethods.size > 0 ? printMethods.filter(m => taggedMethods.has(m)) : printMethods;
  }

  // Chips shown for the ACTIVE TAB, scoped to whichever methods that
  // position's ImageSide rows are actually tagged with. Falls back to the
  // full product-wide list when no position is active yet (nothing staged).
  const methodsForActiveTab = activePositionTab ? methodsForPosition(activePositionTab) : printMethods;

  function togglePosition(position: string) {
    const wasSelected = stagedPositions.includes(position);
    setStagedPositions(prev =>
      wasSelected ? prev.filter(p => p !== position) : [...prev, position]
    );
    if (wasSelected) {
      // Dropping a position also drops its now-meaningless staged
      // method/color entries, so a later re-add starts fresh rather than
      // reviving a stale choice from an earlier, unrelated selection in this
      // same modal session; also bounces the active tab to another staged
      // position (or none) if the dropped one was active, since its own tab
      // no longer exists.
      setStagedMethodByPosition(prev => { const { [position]: _drop, ...rest } = prev; return rest; });
      setStagedColorByPosition(prev => { const { [position]: _drop, ...rest } = prev; return rest; });
      if (activePositionTab === position) {
        setActivePositionTab(stagedPositions.find(p => p !== position) ?? '');
      }
    } else {
      // Newly-added position: jump straight to its own tab so the customer
      // sees an empty picker to fill in immediately, rather than silently
      // staying on whichever other tab was active with no visible cue that
      // this new position needs its own method/color chosen.
      setActivePositionTab(position);
    }
  }

  function pickMethodForActiveTab(method: string) {
    if (!activePositionTab) return;
    setStagedMethodByPosition(prev => ({ ...prev, [activePositionTab]: method }));
  }

  function pickColorForActiveTab(opt: PrintColorOption) {
    if (!activePositionTab) return;
    setStagedColorByPosition(prev => ({ ...prev, [activePositionTab]: opt }));
  }

  function pickAreaSizeForActiveTab(size: number) {
    if (!activePositionTab) return;
    setStagedAreaSizeByPosition(prev => ({ ...prev, [activePositionTab]: size }));
  }

  // The method currently shown/highlighted for whichever tab is active.
  const activeTabMethod = stagedMethodByPosition[activePositionTab];

  // Narrow to whichever print colors the ACTIVE TAB's method actually
  // supports (e.g. PX113334: Screen printing=1-4, Embroidery=1-12,
  // Transfer=1 only), not the flat product-wide list -- this popup lets the
  // customer choose print method and print color together, so it must
  // reflect a color change the instant a different method chip is clicked,
  // before Apply is even pressed. Per-side printColorOptions overrides live
  // on ImageSide (see ImageSideConfig.printColorOptions), scattered across
  // the base product's own imageSides and every color variant's imageSides
  // (any of which the customer might pick for this method) -- collect every
  // side matching the method and use the first one that has an override
  // set, since a real product's sides for one method always agree (loaders
  // only ever tag a side with one method's cap). Falls back to the
  // product-wide list when no method is resolved yet or no matching side has
  // an override (e.g. a product not yet reloaded with per-side data), so
  // nothing regresses for a product this fix hasn't touched yet.
  const methodForColorNarrowing = printPositions.length ? activeTabMethod : stagedMethod;
  const allSidesForStagedMethod = methodForColorNarrowing
    ? allSides.filter(side => side.printMethod === methodForColorNarrowing)
    : [];
  const sideColorOptionsOverride = allSidesForStagedMethod.find(s => s.printColorOptions)?.printColorOptions;
  const printColorOptions = sideColorOptionsOverride
    ? parsePrintColorOptions(sideColorOptionsOverride)
    : productConfig?.printColorOptions ?? [];

  // Similar per-side-override lookup as printColorOptions above, for methods
  // with real physical-size tiers instead of a color-count concept (e.g.
  // MidOcean's Digitale transfer) -- but UNLIKE printColorOptions, this ALSO
  // filters by the active position tab, not just the method. Confirmed live
  // 2026-08-07 that this distinction genuinely matters here (unlike for
  // printColorOptions, whose own comment notes "a real product's sides for
  // one method always agree" -- true for color-count ceilings, but area-size
  // tiers are explicitly DIFFERENT per position for the SAME method, e.g.
  // S00553's Digitale transfer: FRONT/BACK=50/150/300/1176, CHEST=50/100,
  // ARM RIGHT=50/70, INSIDE=49 -- filtering by method alone and taking the
  // first match silently showed FRONT's tiers while the CHEST tab was
  // active, since allSidesForStagedMethod's array order isn't
  // position-aware). No product-wide fallback either, same reasoning as
  // BackgroundColorTool.tsx's own printAreaSizeOptions derivation (the first
  // place this exact position-blindness bug was found and fixed, on the
  // Select Colors wizard step).
  const sideAreaSizeOptionsOverride = activePositionTab
    ? allSidesForStagedMethod.filter(s => s.printPosition === activePositionTab).find(s => s.printAreaSizeOptions)?.printAreaSizeOptions
    : undefined;
  const printAreaSizeOptions = sideAreaSizeOptionsOverride
    ? parsePrintAreaSizeOptions(sideAreaSizeOptionsOverride)
    : [];
  const hasAreaSizeTiers = printAreaSizeOptions.length > 0;

  // The color/area-size tier currently shown/highlighted for the active tab.
  const activeTabColor = stagedColorByPosition[activePositionTab];
  const activeTabAreaSize = stagedAreaSizeByPosition[activePositionTab];

  // Matches every other full-screen modal in the app (ImageCompareModal,
  // VariantSelector, LayerPanel's rename input) — Escape discards staged
  // selections the same way a scrim click does (Apply is required to commit).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Clears a staged print-color pick that no longer appears in the (now
  // method-narrowed) options above -- e.g. "12" staged while Embroidery was
  // selected is no longer valid the instant the customer clicks "Screen
  // printing" (max 4). Without this, Apply could silently commit a
  // print-color value the newly-chosen method doesn't actually support, and
  // no chip would show as selected despite stagedPrintColor still holding
  // the stale value. Only applies to the no-positions single-value path;
  // the per-position path's activeTabColor is naturally re-derived from
  // stagedColorByPosition on every render, so there's no separate stale
  // value to clear there.
  useEffect(() => {
    if (printPositions.length) return;
    if (stagedPrintColor != null && !printColorOptions.includes(stagedPrintColor)) {
      setStagedPrintColor(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stagedMethod]);

  // Mirrors the staged selections into the URL (`?colorVariant=`/`?printMethod=`/
  // `?printColor=`) via replaceState — no navigation/reload, matching every
  // other in-designer URL sync (e.g. useDesignerInit's own cleanup pass). This
  // is what lets a page refresh, or a copied/shared link, reopen the designer
  // with the same three choices already applied (useProductSetup.ts's
  // getRequestedColorVariantNameFromSearch/getRequestedPrintMethodFromSearch/
  // getRequestedPrintColorFromSearch read these back on load). Cleared
  // entirely (not left as a stale param) when a selection type has nothing
  // configured for this product, so a product without color variants never
  // picks up a leftover `?colorVariant=` from an earlier product on the same
  // session/tab.
  const syncSelectionsToUrl = (
    colorId: string | null,
    method: string | null,
    printColor: PrintColorOption | null,
    positions: string[],
    methodByPosition: Record<string, string>,
    colorByPosition: Record<string, PrintColorOption>,
    areaSizeByPosition: Record<string, number>,
  ) => {
    const url = new URL(window.location.href);
    const colorName = colorId ? colorVariants.find(cv => cv.id === colorId)?.colorName : undefined;
    if (colorName) url.searchParams.set('colorVariant', colorName); else url.searchParams.delete('colorVariant');
    if (method) url.searchParams.set('printMethod', method); else url.searchParams.delete('printMethod');
    if (printColor != null) url.searchParams.set('printColor', printColorOptionToString(printColor)); else url.searchParams.delete('printColor');
    if (positions.length) url.searchParams.set('printPositions', positions.join(',')); else url.searchParams.delete('printPositions');
    if (Object.keys(methodByPosition).length) {
      url.searchParams.set('printMethodByPosition', JSON.stringify(methodByPosition));
    } else {
      url.searchParams.delete('printMethodByPosition');
    }
    if (Object.keys(colorByPosition).length) {
      url.searchParams.set('printColorByPosition', JSON.stringify(
        Object.fromEntries(Object.entries(colorByPosition).map(([p, c]) => [p, printColorOptionToString(c)]))
      ));
    } else {
      url.searchParams.delete('printColorByPosition');
    }
    if (Object.keys(areaSizeByPosition).length) {
      url.searchParams.set('printAreaSizeByPosition', JSON.stringify(areaSizeByPosition));
    } else {
      url.searchParams.delete('printAreaSizeByPosition');
    }
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  };

  const handleApply = () => {
    // Only actually switch color variants when the staged pick genuinely
    // differs from what's already active — setActiveColorVariant
    // unconditionally wipes `canvasInstances` (see its own comment: a
    // DIFFERENT variant's sides have different ids, so the old cache/canvas
    // registration is unreachable anyway), which is correct for a real
    // switch but was firing on every single Apply click regardless,
    // including print-method/print-color-only changes on a product with no
    // color variants at all (stagedColorId and activeColorVariantId both
    // null). That wipe drops the just-registered Fabric canvas out of the
    // store with nothing left to re-trigger `useFabricCanvas`'s effect
    // (keyed only on `side?.sideKey`, which never changed here) — the canvas
    // silently never comes back until something else forces a side-key
    // change (e.g. switching side tabs), which is exactly why the customer's
    // very next attempt at the same flow — after a side switch bumps the
    // key — placed the image fine. Reported live as "upload an image, apply
    // print method + colors, then Place on Design silently does nothing the
    // first time."
    if (stagedColorId !== activeColorVariantId) setActiveColorVariant(stagedColorId);
    setSelectedPrintMethod(stagedMethod);
    setSelectedPrintColorOption(stagedPrintColor);
    setSelectedPrintPositions(stagedPositions);
    setSelectedPrintMethodByPosition(stagedMethodByPosition);
    setSelectedPrintColorByPosition(stagedColorByPosition);
    setSelectedPrintAreaSizeByPosition(stagedAreaSizeByPosition);
    syncSelectionsToUrl(stagedColorId, stagedMethod, stagedPrintColor, stagedPositions, stagedMethodByPosition, stagedColorByPosition, stagedAreaSizeByPosition);

    // Tell the parent PDP (when embedded via ?mode=popup) that the customer
    // changed their selection inside the designer, so its own dropdowns/
    // accordion can update to match instead of staying on the stale
    // pre-designer choice. Mirrors syncSelectionsToUrl's own id->name /
    // PrintColorOption->string conversions so the payload speaks the same
    // label vocabulary the PDP already sends inbound via its
    // ?colorVariant=/?printMethod=/... launch params. notifyParent() is
    // already a no-op outside a popup/iframe context, same as the existing
    // DESIGNER_SAVED/DESIGNER_CLOSED call sites.
    //
    // Method/color values are run through translatedMethodLabel/
    // translatedColorOptionLabel before sending — CustomEdge stores the raw
    // vendor-feed value (e.g. Clipper's "TSU"), but Magento's own
    // candidateMethodOptions.title is always the real, already-translated
    // display text (confirmed live 2026-08-06: Magento's title comes from
    // Option::getTitle(), never passed through __() again — it's already
    // store-view-scoped/translated at that layer) — an untranslated
    // "TSU"-shaped payload never matches. Colors have the same gap for a
    // DIFFERENT reason: a binary None/named-value method's only real
    // "color" IS its own value name (e.g. MidOcean's "Lasergravering", no
    // color-count concept at all), so a bare printColorOptionToString
    // wire-string ("1") has nothing to match; only the resolved label does.
    //
    // Position is the ONE field kept RAW (untranslated), not translated —
    // confirmed live 2026-08-06 by comparing against Webfant\DecisionAid\
    // Model\Api\CatalogPdpModel.php (the real customizables-tree builder):
    // it keys its own subgrouplabel by `$option->getSubgroupLabel()`
    // directly, with NO __() call, so Magento's own candidateMethodOptions
    // `position` field is genuinely raw/untranslated on the Magento side
    // too — unlike group_label (which DOES get __()'d there). Whether that
    // raw value happens to already be Dutch (Clipper: "Paneel 1") or stays
    // vendor-English (MidOcean: "FRONT SILVER PART") depends entirely on
    // what that vendor's import job originally wrote to subgroup_label, not
    // on any translation step — so there is no single "correct" transform
    // to apply here; sending the untranslated designer-side value and
    // letting the PDP-side resolver try both raw and Magento-translated
    // forms (see applyDesignerSelectionChange's positionCandidates) is the
    // only shape that works for both vendors at once.
    const stagedColorName = stagedColorId ? colorVariants.find(cv => cv.id === stagedColorId)?.colorName : undefined;
    const translatedMethodByPosition = Object.fromEntries(
      Object.entries(stagedMethodByPosition).map(([p, m]) => [p, translatedMethodLabel(productConfig, m) ?? m])
    );
    const translatedColorByPosition = Object.fromEntries(
      Object.entries(stagedColorByPosition).map(([p, c]) => [
        p,
        translatedColorOptionLabel(productConfig, stagedMethodByPosition[p], c, t('designer.options.fullColor')),
      ])
    );
    // Area-size tiers have no printColorOptionsTranslated table entry to
    // resolve through (that map is keyed by parsed color-count/full-color
    // strings, an area-size cm2 number was never one of those) -- built
    // directly as the exact literal string Magento's own candidateMethodOptions
    // values use for these tiers ("Full color max {N} cm2", confirmed live
    // 2026-08-07 against S00553's real values), so the PDP-side resolver's
    // EXACT-match path (findColorValueIdByTitle's first branch, before its
    // parsed-count fallback) matches it directly -- no PDP-side changes
    // needed for this to work, same wire contract as printColorByPosition.
    const translatedAreaSizeByPosition = Object.fromEntries(
      Object.entries(stagedAreaSizeByPosition).map(([p, size]) => [p, `Full color max ${size} cm2`])
    );
    notifyParent('DESIGNER_SELECTION_CHANGED', {
      colorVariant: stagedColorName ?? null,
      printMethod: stagedMethod ? (translatedMethodLabel(productConfig, stagedMethod) ?? stagedMethod) : null,
      printColor: stagedPrintColor != null
        ? translatedColorOptionLabel(productConfig, stagedMethod, stagedPrintColor, t('designer.options.fullColor'))
        : null,
      printPositions: stagedPositions,
      printPositionsTranslated: stagedPositions.map(p => translatedPositionLabel(productConfig, p) ?? p),
      printMethodByPosition: translatedMethodByPosition,
      // Area-size positions merged INTO printColorByPosition (not a separate
      // payload field) -- the PDP resolver already reads printColorByPosition
      // as "whatever label this position's method should resolve to", and an
      // area-size tier's label IS exactly that shape (a candidate value
      // title to match), so no PDP-side schema change is needed. A position
      // can only ever be in ONE of stagedColorByPosition/stagedAreaSizeByPosition
      // at a time in practice (hasAreaSizeTiers gates which chip set even
      // renders for a given method), so this merge never actually collides.
      printColorByPosition: { ...translatedColorByPosition, ...translatedAreaSizeByPosition },
    });

    onClose();
  };

  const chipStyle = (active: boolean): React.CSSProperties => ({
    padding: '6px 14px',
    borderRadius: 16,
    fontSize: 13,
    cursor: 'pointer',
    border: active ? '1.5px solid var(--selection)' : '1px solid #e2e8f0',
    background: active ? 'var(--selection-tint)' : 'white',
    color: active ? 'var(--selection-text)' : '#374151',
  });

  return (
    <div
      onClick={() => { if (!window.matchMedia('(max-width: 767px)').matches) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 10001,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        className="designer-product-options-dialog"
        onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 10, padding: 20, width: 720, maxWidth: '90vw', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}
      >
        {colorVariants.length === 0 && (
          <button type="button" className="designer-product-options-close" aria-label="Close product options" onClick={onClose}>×</button>
        )}
        {colorVariants.length > 0 && (
          <section style={{ marginBottom: 18 }}>
            <div className="designer-product-options-heading">
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                {t('designer.options.productColor')}
              </label>
              <button type="button" className="designer-product-options-close" aria-label="Close product options" onClick={onClose}>×</button>
            </div>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
              {colorVariants.map(cv => (
                <button
                  key={cv.id}
                  onClick={() => setStagedColorId(cv.id)}
                  title={capitalizeLabel(cv.colorName)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    width: 100,boxSizing: 'border-box', border: stagedColorId === cv.id ? '2px solid var(--selection)' : '1px solid #e2e8f0',
                    borderRadius: 8, padding: 2, flexShrink: 0, cursor: 'pointer', background: 'none',
                  }}
                >
                  {cv.swatchImageUrl ? (
                    // `contain` (not `cover`) — the vendor swatch is usually a full
                    // product photo (e.g. a tall bottle), not a close-up color
                    // swatch; `cover` on a 56x56 square cropped out the cap/base and
                    // showed only the midsection, making it hard to recognize which
                    // color/product this even was.
                    <img
                      src={cv.swatchImageUrl}
                      alt=""
                      // See crossOriginImage.ts — this can be the exact same S3
                      // object as a side's own imagePath, so it must resolve
                      // crossOrigin identically or risk the documented CORS
                      // cache-race (this modal can be open at the same time as
                      // DesignerWorkspace/DesignerHeader, both of which load it).
                      crossOrigin={crossOriginForUrl(cv.swatchImageUrl)}
                      style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 6, display: 'block' }}
                    />
                  ) : (
                    <div style={{ width: 56, height: 56, borderRadius: 6, background: cv.colorHex ?? '#e2e8f0' }} />
                  )}
                  <span style={{
                    fontSize: 12, fontWeight: 500, textAlign: 'center', lineHeight: 1.2,
                    color: stagedColorId === cv.id ? 'var(--selection-text)' : '#64748b',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
                  }}>
                    {capitalizeLabel(cv.colorName)}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}

        {printPositions.length > 0 && (
          <section style={{ marginBottom: 18 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>
              {t('designer.options.printPosition')}
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: stagedPositions.length ? 12 : 0 }}>
              {printPositions.map(p => (
                <button key={p} onClick={() => togglePosition(p)} style={chipStyle(stagedPositions.includes(p))}>
                  {translatedPositionLabel(productConfig, p) ?? p}
                </button>
              ))}
            </div>

            {stagedPositions.length > 0 && (
              <>
                {/* One tab per staged position, each with its own independent
                    method+color — print method depends on print position and
                    print color depends on the chosen method, so there is no
                    single "apply to all" shortcut here. Switching tabs just
                    changes which entry pickMethodForActiveTab/
                    pickColorForActiveTab writes into below — no data is lost
                    switching between tabs, only the currently-EDITED entry
                    changes. */}
                <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e2e8f0', marginBottom: 12 }}>
                  {stagedPositions.map(position => (
                    <button
                      key={position}
                      onClick={() => setActivePositionTab(position)}
                      style={{
                        padding: '6px 12px', border: 'none', background: 'none', cursor: 'pointer',
                        fontSize: 13, fontWeight: activePositionTab === position ? 700 : 500,
                        color: activePositionTab === position ? 'var(--selection-text)' : '#64748b',
                        borderBottom: activePositionTab === position ? '2px solid var(--selection)' : '2px solid transparent',
                        marginBottom: -1,
                      }}
                    >
                      {position}
                    </button>
                  ))}
                </div>

                {methodsForActiveTab.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>
                      {t('designer.options.printMethod')}
                    </label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {methodsForActiveTab.map(m => (
                        <button key={m} onClick={() => pickMethodForActiveTab(m)} style={chipStyle(activeTabMethod === m)}>
                          {translatedMethodLabel(productConfig, m) ?? m}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {!hasAreaSizeTiers && printColorOptions.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>
                      {t('designer.options.printColors')}
                    </label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {printColorOptions.map(opt => (
                        <button
                          key={printColorOptionToString(opt)}
                          onClick={() => pickColorForActiveTab(opt)}
                          style={chipStyle(activeTabColor === opt)}
                        >
                          {translatedColorOptionLabel(productConfig, methodForColorNarrowing, opt, t('designer.options.fullColor'))}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* This method has real physical-print-SIZE tiers instead of
                    a color-count concept (e.g. MidOcean's Digitale transfer)
                    -- separate chip row, mutually exclusive with the
                    color-count chips above (hasAreaSizeTiers gates which one
                    renders, never both). */}
                {hasAreaSizeTiers && (
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>
                      {t('designer.options.printAreaSize')}
                    </label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {printAreaSizeOptions.map(size => (
                        <button
                          key={size}
                          onClick={() => pickAreaSizeForActiveTab(size)}
                          style={chipStyle(activeTabAreaSize === size)}
                        >
                          {t('designer.options.areaSizeCm2', { size })}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {printPositions.length === 0 && printMethods.length > 0 && (
          <section style={{ marginBottom: 18 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>
              {t('designer.options.printMethod')}
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {printMethods.map(m => (
                <button key={m} onClick={() => setStagedMethod(m)} style={chipStyle(stagedMethod === m)}>
                  {translatedMethodLabel(productConfig, m) ?? m}
                </button>
              ))}
            </div>
          </section>
        )}

        {/* NOTE: this no-positions path (products with printMethods but no
            printPositions at all) has NO area-size-tier equivalent wired in
            -- confirmed live 2026-08-07 that MidOcean, the only vendor with
            any area-size tier data, has printPositions set on 100% of its
            catalog, so this branch never actually needs it. Revisit if a
            future vendor's area-size data shows up on a positions-less
            product. */}
        {printPositions.length === 0 && printColorOptions.length > 0 && (
          <section style={{ marginBottom: 22 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>
              {t('designer.options.printColors')}
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {printColorOptions.map(opt => (
                <button
                  key={printColorOptionToString(opt)}
                  onClick={() => setStagedPrintColor(opt)}
                  style={chipStyle(stagedPrintColor === opt)}
                >
                  {translatedColorOptionLabel(productConfig, methodForColorNarrowing, opt, t('designer.options.fullColor'))}
                </button>
              ))}
            </div>
          </section>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleApply}
            className="btn btn-primary"
            style={{ padding: '8px 22px', fontSize: 14 }}
          >
            {t('designer.options.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}
